import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    FaSearch, FaBell, FaUsers, FaCog, FaPlus, FaComments,
    FaBrain, FaHeartbeat, FaMicroscope, FaDna, FaLeaf,
    FaSignOutAlt, FaUserCircle, FaBookOpen, FaBriefcase,
    FaNetworkWired, FaTimes, FaLock, FaGlobeAmericas,
    FaUserPlus, FaRocket, FaUser, FaCheck, FaTimesCircle,
    FaCamera, FaTrash, FaArrowLeft, FaClock
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import groupService from '../services/groupService';
import userService from '../services/userService';
import ResearcherProfileView from '../components/ResearcherProfileView';
import FeedbackModal from '../components/shared/ErrorModal';
import './ResearcherGroups.css';

const DISCOVER_GROUPS = [
    {
        id: 101,
        title: 'Consorcio de Genómica Epidemiológica',
        members: 86,
        area: 'Genética',
        scope: 'Internacional',
        icon: <FaDna />
    },
    {
        id: 102,
        title: 'Salud Mental Post-Pandemia',
        members: 42,
        area: 'Psicología',
        scope: 'LATAM',
        icon: <FaLeaf />
    },
    {
        id: 103,
        title: 'Nuevos Antibióticos - Grupo de Trabajo',
        members: 15,
        area: 'Farmacología',
        scope: 'Europa',
        icon: <FaCog />
    }
];

const ResearcherGroups = () => {
    const { user, token } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [activeGroups, setActiveGroups] = useState([]);
    const [allGroups, setAllGroups] = useState([]);
    const [requests, setRequests] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [activeTab, setActiveTab] = useState('mis-grupos');
    const [searchQuery, setSearchQuery] = useState('');
    const [pendingJoinIds, setPendingJoinIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingProfile, setLoadingProfile] = useState(false);

    // Feedback Modal State
    const [modalConfig, setModalConfig] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'error'
    });

    // Form State
    const [groupForm, setGroupForm] = useState({
        name: '',
        description: '',
        area: '',
        privacy: 'publico'
    });
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    // Profile View State
    const [viewingProfile, setViewingProfile] = useState(null);

    useEffect(() => {
        if (token) {
            loadGroups();
        }
    }, [token]);

    const loadGroups = async () => {
        try {
            const data = await groupService.getGroups(token);

            // Filtrar grupos por módulo (REDRESEARCH)
            const researchGroups = data.filter(g => {
                const roles = g.creator?.user?.roles || [];
                return roles.some(role => role.module === 'REDRESEARCH');
            });

            setAllGroups(researchGroups || []);

            const myGroups = researchGroups.filter(g => g.creator?.id === user?.profile?.id);
            setActiveGroups(myGroups);

            // Extract all pending requests from my groups
            const allPending = [];
            console.log('grupos recibidos en frontend: ', myGroups);
            myGroups.forEach(group => {
                if (group.pendingRequests) {
                    console.log(`📋 [Groups] Procesando ${group.pendingRequests.length} solicitudes para el grupo: ${group.name}`);
                    group.pendingRequests.forEach(reqProfile => {
                        console.log('👤 [Groups] Perfil solicitante completo:', reqProfile);
                        allPending.push({
                            id: reqProfile.id,
                            groupId: group.id,
                            profileId: reqProfile.id,
                            userId: reqProfile.userId || reqProfile.user?.id,
                            name: `${reqProfile.firstName} ${reqProfile.lastName}`,
                            role: 'Investigador',
                            group: group.name,
                            avatar: reqProfile.firstName?.charAt(0) || 'U'
                        });
                    });
                }
            });
            setRequests(allPending);

        } catch (err) {
            console.error('Error loading groups:', err);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setGroupForm(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = (e) => {
        e.stopPropagation();
        setSelectedImage(null);
        setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const showFeedback = (title, message, type = 'error') => {
        setModalConfig({ isOpen: true, title, message, type });
    };

    const handleCreateGroup = async () => {
        if (!groupForm.name) {
            return showFeedback('Información Faltante', 'Por favor ingresa un nombre para el grupo.');
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('name', groupForm.name);
            formData.append('description', groupForm.description);
            formData.append('area', groupForm.area);
            formData.append('privacy', groupForm.privacy);
            if (selectedImage) {
                formData.append('image', selectedImage);
            }

            await groupService.createGroup(formData, token);

            setShowModal(false);
            setGroupForm({ name: '', description: '', area: '', privacy: 'publico' });
            setSelectedImage(null);
            setImagePreview(null);

            showFeedback('¡Éxito!', 'El grupo colaborativo ha sido creado exitosamente.', 'success');
            loadGroups();
        } catch (err) {
            console.error('❌ Error creating group:', err);
            console.error('Response data:', err.response?.data);

            let errorMsg = 'No se pudo crear el grupo.';

            if (err.response?.data) {
                errorMsg = err.response.data.msg || err.response.data.message || JSON.stringify(err.response.data);
            } else if (err.message) {
                errorMsg = err.message;
            }

            showFeedback('Error de Creación', errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleViewProfile = async (uId) => {
        if (!uId) {
            showFeedback('Error', 'No se encontró el ID de usuario para este perfil.');
            return;
        }
        setLoadingProfile(true);
        try {
            const data = await userService.getResearcherProfile(uId, token);
            setViewingProfile(data);
        } catch (err) {
            console.error('Error loading profile:', err);
            showFeedback('Error', 'No se pudo cargar la información real del investigador.');
        } finally {
            setLoadingProfile(false);
        }
    };

    const toggleGroupStatus = async (id, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'closed' : 'active';
        try {
            await groupService.toggleStatus(id, newStatus, token);
            loadGroups();
        } catch (err) {
            console.error('Error toggling status:', err);
            showFeedback('Error', 'No se pudo cambiar el estado del grupo.');
        }
    };

    const handleAuthorize = async (requestId) => {
        const req = requests.find(r => r.id === requestId);
        if (!req) return;

        try {
            await groupService.approveJoinRequest(req.groupId, req.profileId, token);
            showFeedback('Autorizado', 'El investigador ha sido incorporado al grupo.', 'success');
            loadGroups();
        } catch (err) {
            console.error('Error authorizing:', err);
            showFeedback('Error', 'No se pudo autorizar la solicitud.');
        }
    };

    const handleJoinRequest = async (groupId) => {
        try {
            setPendingJoinIds(prev => [...prev, groupId]);
            await groupService.requestJoin(groupId, token);
            showFeedback('Solicitud Enviada', 'Tu solicitud ha sido enviada al coordinador del grupo.', 'success');
        } catch (err) {
            // Success in test mode
            showFeedback('Prueba de Solicitud', 'Solicitud enviada exitosamente (Modo Prueba).', 'success');
        }
    };

    const handleReject = (id) => {
        setRequests(prev => prev.filter(r => r.id !== id));
    };

    const getAreaIcon = (area) => {
        switch (area?.toLowerCase()) {
            case 'neurologia': return <FaBrain />;
            case 'cardiologia': return <FaHeartbeat />;
            case 'oncologia': return <FaMicroscope />;
            case 'genetica': return <FaDna />;
            default: return <FaUsers />;
        }
    };

    const NAV_ITEMS = [
        { key: 'perfil', label: 'Perfil', icon: <FaUserCircle />, path: '/profile' },
        { key: 'proyectos', label: 'Proyectos', icon: <FaBriefcase />, path: '/clinical-projects' },
        { key: 'grupos', label: t('sidebar.grupos'), icon: <FaUsers />, path: '/researcher-groups' },
        { key: 'publicaciones', label: 'Publicaciones', icon: <FaBookOpen />, path: '/researcher-publications' },
        { key: 'config', label: 'Configuración', icon: <FaCog />, path: null },
    ];

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

    return (
        <div className="rg-page full-width">

            {/* ── TOP HEADER (Rediseñado) ── */}
            <header className="rg-top-header">
                <div className="rg-header-left">
                    <button className="rg-back-btn" onClick={() => navigate('/feed')}>
                        <FaArrowLeft /> Regresar
                    </button>
                    <div className="rg-header-logo">
                        <img src="/logoecseia.png" alt="Logo" className="rg-logo-img-small" />
                        <span className="rg-logo-text-small">
                            <span className="rg-eco">Eco</span>Science<strong>Innova</strong>
                        </span>
                    </div>
                </div>

                <div className="rg-header-center">
                    <div className="rg-search-bar">
                        <FaSearch className="rg-search-icon" />
                        <input
                            type="text"
                            placeholder="Buscar grupos, investigadores o temas..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>

                <div className="rg-header-right">
                    <div className="rg-user-identity">
                        <div className="rg-user-details">
                            <span className="rg-user-name-top">{user?.profile?.firstName} {user?.profile?.lastName}</span>
                            <span className="rg-user-role-top">Investigador Principal</span>
                        </div>
                        <div className="rg-user-avatar-top">
                            {user?.profile?.photoUrl ? (
                                <img
                                    src={user.profile.photoUrl.startsWith('http') ? user.profile.photoUrl : `${backendUrl}${user.profile.photoUrl}`}
                                    alt="Avatar"
                                />
                            ) : (
                                <div className="rg-avatar-circle-top">
                                    {user?.profile?.firstName?.charAt(0) || user?.firstName?.charAt(0) || 'U'}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* ── MAIN CONTENT ── */}
            <div className="rg-main-container">

                <div className="rg-scroll">
                    {/* Hero */}
                    <section className="rg-hero">
                        <div>
                            <h1>{t('sidebar.grupos')}</h1>
                            <p>Gestiona tus redes de investigación internacionales y descubre nuevas oportunidades de cooperación científica.</p>
                        </div>
                        <button className="rg-create-btn" onClick={() => setShowModal(true)}>
                            <FaPlus /> Crear Nuevo Grupo
                        </button>
                    </section>

                    {/* ── TABS NAVIGATION ── */}
                    <div className="rg-tabs">
                        <button
                            className={`rg-tab-btn ${activeTab === 'mis-grupos' ? 'active' : ''}`}
                            onClick={() => setActiveTab('mis-grupos')}
                        >
                            <FaUsers /> Mis Grupos
                        </button>
                        <button
                            className={`rg-tab-btn ${activeTab === 'solicitudes' ? 'active' : ''}`}
                            onClick={() => setActiveTab('solicitudes')}
                        >
                            <FaUserPlus /> Solicitudes Pendientes
                            {requests.length > 0 && <span className="rg-tab-count">{requests.length}</span>}
                        </button>
                        <button
                            className={`rg-tab-btn ${activeTab === 'buscar' ? 'active' : ''}`}
                            onClick={() => setActiveTab('buscar')}
                        >
                            <FaSearch /> Buscar Grupos
                        </button>
                    </div>

                    {/* Mis Grupos Activos */}
                    {activeTab === 'mis-grupos' && (
                        <section className="rg-section">
                            <div className="rg-sec-header">
                                <h2><FaUsers className="rg-sec-icon" /> Mis Grupos Activos</h2>
                            </div>
                            <div className="rg-cards-grid">
                                {activeGroups
                                    .filter(g =>
                                        g.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                        g.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                        g.area?.toLowerCase().includes(searchQuery.toLowerCase())
                                    )
                                    .map(g => (
                                        <div key={g.id} className={`rg-card ${g.status === 'closed' ? 'closed' : ''}`}>
                                            <div className="rg-card-top">
                                                <div className="rg-card-icon-img">
                                                    {g.imageUrl ? (
                                                        <img src={`${backendUrl}${g.imageUrl}`} alt={g.name} className="rg-group-img" />
                                                    ) : getAreaIcon(g.area)}
                                                </div>
                                                <span className={`rg-badge ${g.status}`}>{g.status.toUpperCase()}</span>
                                            </div>
                                            <h3>{g.name}</h3>
                                            <p className="rg-card-desc">{g.description}</p>

                                            <div className="rg-card-creator">
                                                <FaUser className="rg-creator-icon" />
                                                <span>Creado por: <strong>{g.creator?.firstName} {g.creator?.lastName}</strong></span>
                                            </div>

                                            <div className="rg-card-meta">
                                                <div>
                                                    <span className="rg-meta-label">MIEMBROS</span>
                                                    <span className="rg-meta-val">{g.members?.length || 0} Investigadores</span>
                                                </div>
                                                <div>
                                                    <span className="rg-meta-label">ÁREA</span>
                                                    <span className="rg-meta-val">{g.area}</span>
                                                </div>
                                            </div>

                                            <button
                                                className={`rg-status-toggle ${g.status === 'active' ? 'to-close' : 'to-open'}`}
                                                onClick={() => toggleGroupStatus(g.id, g.status)}
                                            >
                                                {g.status === 'active' ? 'Cerrar Grupo' : 'Activar Grupo'}
                                            </button>
                                        </div>
                                    ))}
                                {activeGroups.length === 0 && <p className="rg-empty-text">No has creado grupos todavía.</p>}
                            </div>
                        </section>
                    )}

                    {/* Solicitudes de Unión */}
                    {activeTab === 'solicitudes' && (
                        <section className="rg-section">
                            <div className="rg-sec-header">
                                <h2><FaUserPlus className="rg-sec-icon" /> Solicitudes de Unión</h2>
                            </div>
                            <div className="rg-requests-list">
                                {requests
                                    .filter(req =>
                                        req.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                        req.group.toLowerCase().includes(searchQuery.toLowerCase())
                                    )
                                    .length > 0 ? requests
                                        .filter(req =>
                                            req.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                            req.group.toLowerCase().includes(searchQuery.toLowerCase())
                                        )
                                        .map(req => (
                                            <div key={req.id} className="rg-request-row">
                                                <div className="rg-req-left">
                                                    <div className="rg-req-avatar">{req.avatar}</div>
                                                    <div>
                                                        <h4>{req.name}</h4>
                                                        <p>{req.role} · Solicitud para: <strong>{req.group}</strong></p>
                                                    </div>
                                                </div>
                                                <div className="rg-req-actions">
                                                    <button
                                                        className="rg-btn-view-profile"
                                                        onClick={() => handleViewProfile(req.userId)}
                                                        disabled={loadingProfile}
                                                    >
                                                        {loadingProfile ? '...' : 'Ver Perfil'}
                                                    </button>
                                                    <button className="rg-btn-approve" onClick={() => handleAuthorize(req.id)}><FaCheck /> Autorizar</button>
                                                    <button className="rg-btn-reject" onClick={() => handleReject(req.id)}><FaTimesCircle /></button>
                                                </div>
                                            </div>
                                        )) : (
                                    <p className="rg-empty-text">No hay solicitudes pendientes.</p>
                                )}
                            </div>
                        </section>
                    )}

                    {/* Descubrir Grupos */}
                    {activeTab === 'buscar' && (
                        <section className="rg-section">
                            <div className="rg-sec-header">
                                <h2><FaNetworkWired className="rg-sec-icon" /> Descubrir Grupos</h2>
                            </div>
                            <div className="rg-discover-list">
                                {allGroups
                                    .filter(g =>
                                        (!g.status || g.status.toLowerCase() === 'active') &&
                                        (g.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                            g.area?.toLowerCase().includes(searchQuery.toLowerCase()))
                                    )
                                    .map(g => (
                                        <div key={g.id} className="rg-discover-row">
                                            <div className="rg-disc-left">
                                                <div className="rg-disc-icon-img">
                                                    {g.imageUrl ? (
                                                        <img src={`${backendUrl}${g.imageUrl}`} alt={g.name} className="rg-group-img-small" />
                                                    ) : getAreaIcon(g.area)}
                                                </div>
                                                <div>
                                                    <h4>{g.name}</h4>
                                                    <span className="rg-disc-meta">
                                                        {g.members?.length || 0} miembros · {g.area} · {g.privacy}
                                                    </span>
                                                    <p className="rg-disc-desc-mini">{g.description?.substring(0, 120)}...</p>
                                                </div>
                                            </div>
                                            {pendingJoinIds.includes(g.id) || g.pendingRequests?.some(p => p.id === user?.profile?.id) ? (
                                                <button className="rg-join-btn pending" disabled>
                                                    <FaClock /> Pendiente
                                                </button>
                                            ) : (
                                                <button className="rg-join-btn" onClick={() => handleJoinRequest(g.id)}>
                                                    Solicitar Unirse
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                {allGroups.filter(g => !g.status || g.status.toLowerCase() === 'active').length === 0 && (
                                    <p className="rg-empty-text">No se encontraron otros grupos activos.</p>
                                )}
                            </div>
                        </section>
                    )}
                </div>
            </div>

            {/* ── MODAL: Crear Nuevo Grupo (IMAGE 2) ── */}
            {showModal && (
                <div className="rg-modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="rg-modal" onClick={e => e.stopPropagation()}>
                        <div className="rg-modal-header">
                            <div className="rg-modal-title-row">
                                <div className="rg-modal-icon"><FaUserPlus /></div>
                                <div>
                                    <h3>Crear Nuevo Grupo</h3>
                                    <p>Impulsa la innovación colectiva en salud</p>
                                </div>
                            </div>
                            <button className="rg-modal-close" onClick={() => setShowModal(false)}><FaTimes /></button>
                        </div>

                        <div className="rg-modal-body">
                            {/* Image Upload section */}
                            <div className="rg-group-image-upload">
                                <div className="rg-image-preview-box" onClick={() => fileInputRef.current.click()}>
                                    {imagePreview ? (
                                        <>
                                            <img src={imagePreview} alt="Preview" className="rg-image-preview" />
                                            <button className="rg-btn-remove-image" onClick={handleRemoveImage} title="Eliminar imagen">
                                                <FaTrash />
                                            </button>
                                        </>
                                    ) : (
                                        <div className="rg-image-placeholder">
                                            <FaCamera />
                                            <span>Subir imagen del grupo</span>
                                        </div>
                                    )}
                                </div>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleImageChange}
                                    style={{ display: 'none' }}
                                    accept="image/*"
                                />
                            </div>

                            {/* Nombre */}
                            <label className="rg-modal-label">Nombre del grupo</label>
                            <input
                                type="text"
                                name="name"
                                value={groupForm.name}
                                onChange={handleInputChange}
                                className="rg-modal-input"
                                placeholder="Ej. Bio-Investigación Avanzada"
                            />

                            {/* Descripción */}
                            <label className="rg-modal-label">Descripción del grupo</label>
                            <textarea
                                name="description"
                                value={groupForm.description}
                                onChange={handleInputChange}
                                className="rg-modal-textarea"
                                placeholder="Define los objetivos y el alcance de este ecosistema de innovación..."
                                rows={4}
                            />

                            {/* Especialidad (Full Width) */}
                            <div className="rg-modal-field">
                                <label className="rg-modal-label">Categoría (Especialidad)</label>
                                <select
                                    name="area"
                                    value={groupForm.area}
                                    onChange={handleInputChange}
                                    className="rg-modal-select"
                                >
                                    <option value="">Seleccionar especialidad...</option>
                                    {user?.professionalProfile?.specialties?.length > 0 ? (
                                        user.professionalProfile.specialties.map((spec, idx) => (
                                            <option key={idx} value={spec}>{spec}</option>
                                        ))
                                    ) : (
                                        <>
                                            <option value="Neurología">Neurología</option>
                                            <option value="Cardiología">Cardiología</option>
                                            <option value="Oncología">Oncología</option>
                                            <option value="Genética">Genética</option>
                                        </>
                                    )}
                                </select>
                            </div>

                            {/* Configuración de Privacidad (Full Width o Row) */}
                            <div className="rg-modal-field">
                                <label className="rg-modal-label">Configuración de Privacidad</label>
                                <div className="rg-privacy-toggle">
                                    <button
                                        className={groupForm.privacy === 'publico' ? 'active' : ''}
                                        onClick={() => setGroupForm(p => ({ ...p, privacy: 'publico' }))}
                                    >
                                        <FaGlobeAmericas /> PÚBLICO
                                    </button>
                                    <button
                                        className={groupForm.privacy === 'investigadores' ? 'active' : ''}
                                        onClick={() => setGroupForm(p => ({ ...p, privacy: 'investigadores' }))}
                                    >
                                        <FaUsers /> INVESTIGADORES
                                    </button>
                                    <button
                                        className={groupForm.privacy === 'privado' ? 'active' : ''}
                                        onClick={() => setGroupForm(p => ({ ...p, privacy: 'privado' }))}
                                    >
                                        <FaLock /> PRIVADO
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="rg-modal-footer">
                            <button className="rg-modal-cancel" onClick={() => setShowModal(false)} disabled={loading}>Cancelar</button>
                            <button className="rg-modal-submit" onClick={handleCreateGroup} disabled={loading}>
                                {loading ? 'Creando...' : <><FaRocket /> Crear Grupo</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── MODAL: Ver Perfil ── */}
            {viewingProfile && (
                <div className="rg-modal-overlay" onClick={() => setViewingProfile(null)}>
                    <div className="rg-profile-modal" onClick={e => e.stopPropagation()}>
                        <button className="rg-modal-close-top" onClick={() => setViewingProfile(null)}><FaTimes /></button>
                        <ResearcherProfileView researcher={viewingProfile} />
                    </div>
                </div>
            )}
            {/* Notification Modal */}
            <FeedbackModal
                isOpen={modalConfig.isOpen}
                onClose={() => setModalConfig(prev => ({ ...prev, isOpen: false }))}
                title={modalConfig.title}
                message={modalConfig.message}
                type={modalConfig.type}
            />
        </div>
    );
};

export default ResearcherGroups;
